import { useState } from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { TONE_CLASSES, toneAt } from '../analytics/chartTheme';

interface ReportQuote {
  text: string;
  context: string;
  turnIndex: number;
  timestamp: number;
  sentiment?: 'positive' | 'negative' | 'neutral';
  /** Respondent attribution for aggregated reports */
  respondentName?: string;
}

interface QuoteSpotlightProps {
  quotes: ReportQuote[];
  onQuoteClick?: (turnIndex: number, timestamp?: number) => void;
}

/** Quote cards per page, the same as the Overview's highlights. */
const PAGE_SIZE = 2;

/** Arrow buttons, matching the highlights carousel. */
const ARROW = 'rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring';

/** `mm:ss` for a quote's position in the conversation. */
function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Key quotes as a carousel that matches the Overview's highlights: two toned
 * cards per page, paged with dots and arrows.
 */
export function QuoteSpotlight({ quotes, onQuoteClick }: QuoteSpotlightProps) {
  const [page, setPage] = useState(0);
  if (!quotes.length) return null;
  const pages = Math.ceil(quotes.length / PAGE_SIZE);
  const current = Math.min(page, pages - 1);
  const start = current * PAGE_SIZE;
  const visible = quotes.slice(start, start + PAGE_SIZE);

  return (
    <section aria-label="Key quotes" className="relative flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">Key quotes</h2>
        {pages > 1 && (
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="flex gap-1.5">
              {Array.from({ length: pages }, (_, index) => (
                <span key={index} className={`h-1.5 w-1.5 rounded-full ${index === current ? 'bg-accent-soft' : 'bg-border'}`} />
              ))}
            </span>
            <span className="sr-only">{`${current + 1} / ${pages}`}</span>
            <button type="button" aria-label="Previous quotes" className={ARROW} onClick={() => setPage((current - 1 + pages) % pages)}>
              <IconChevronLeft aria-hidden="true" className="h-4 w-4" />
            </button>
            <button type="button" aria-label="Next quotes" className={ARROW} onClick={() => setPage((current + 1) % pages)}>
              <IconChevronRight aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        )}
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {visible.map((quote, index) => {
          const meta = [quote.respondentName, quote.context, quote.timestamp != null ? formatTimestamp(quote.timestamp) : null].filter(Boolean).join(' · ');
          return (
            <article
              key={start + index}
              className={`${TONE_CLASSES[toneAt(start + index)]} flex flex-col gap-3 rounded-[20px] p-6${onQuoteClick ? ' cursor-pointer transition-transform hover:-translate-y-0.5' : ''}`}
              onClick={onQuoteClick ? () => onQuoteClick(quote.turnIndex, quote.timestamp) : undefined}
            >
              <span aria-hidden="true" className="block h-6 select-none font-serif text-5xl leading-[0.9] text-[var(--chart-mark)]">
                &ldquo;
              </span>
              <blockquote className="text-[15px] font-medium leading-relaxed text-foreground">{quote.text}</blockquote>
              {meta && <p className="mt-auto text-sm text-muted-foreground">{meta}</p>}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default QuoteSpotlight;
